import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { GrainArt } from '@/components/products/grain-art'
import { ProcessSection } from '@/components/landing/process-section'
import { CtaSection } from '@/components/landing/cta-section'
import { siteConfig } from '@/constants/site'

import {
    CalendarCheck,
    Handshake,
    Scale,
    ShieldCheck
} from 'lucide-react'

const VALUES = [
    {
        icon: ShieldCheck,
        title: 'Specification first',
        text: 'We quote against a written specification and ship against it. The lab report travels with the goods.'
    },
    {
        icon: Scale,
        title: 'Clear, fair pricing',
        text: 'Every quotation spells out packing, delivery terms and validity, so there are no surprises on the invoice.'
    },
    {
        icon: CalendarCheck,
        title: 'Supply you can plan around',
        text: 'Stock held in fumigated warehouses and planned dispatches keep your shelves and kitchens supplied all season.'
    },
    {
        icon: Handshake,
        title: 'Partnerships, not one-off deals',
        text: 'We would rather keep a buyer for years than win a single order. Regular buyers get priority allocation and agreed terms.'
    }
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description={`${siteConfig.name} is a wholesale rice trading house supplying graded, lab-checked basmati, non-basmati, parboiled and broken rice to distributors, retailers, food service and importers.`}
            />

            <section id='about-intro' aria-labelledby='about-title' className='border-b border-border bg-secondary/60'>
                <Container className='grid items-center gap-12 py-16 sm:py-20 lg:grid-cols-12 lg:gap-16 lg:py-24'>
                    <div className='lg:col-span-7'>
                        <SectionHeading
                            as='h1'
                            id='about-title'
                            eyebrow='About us'
                            title='A rice trading house buyers can plan around'
                            description={`${siteConfig.name} buys rice from established mills, checks every lot and supplies it in bulk to distributors, retailers, food service businesses and importers.`}
                        />
                    </div>

                    <div className='lg:col-span-5'>
                        <figure className='relative aspect-[4/3] overflow-hidden rounded-[2rem] shadow-xl shadow-primary/15'>
                            <GrainArt
                                seed='about-golden-paddy'
                                length='long'
                                tone='golden'
                                backdrop='forest'
                                className='absolute inset-0 size-full'
                            />

                            <figcaption className='absolute right-4 bottom-4 left-4 rounded-2xl bg-card/95 p-5 shadow-lg backdrop-blur sm:right-5 sm:bottom-5 sm:left-5'>
                                <p className='font-heading text-lg leading-snug font-medium text-foreground'>
                                    &ldquo;The right grade, packed the right way, delivered when we said it would be.&rdquo;
                                </p>

                                <p className='mt-2 text-xs font-bold tracking-[0.16em] text-bronze uppercase'>
                                    Our promise to every buyer
                                </p>
                            </figcaption>
                        </figure>
                    </div>
                </Container>
            </section>

            <section id='our-approach' aria-labelledby='our-approach-title' className='py-20 sm:py-24 lg:py-28'>
                <Container className='grid gap-10 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            id='our-approach-title'
                            eyebrow='What we do'
                            title='We take the risk out of buying rice'
                        />
                    </div>

                    <div className='space-y-6 text-base leading-relaxed text-muted-foreground sm:text-lg lg:col-span-7'>
                        <p>
                            Rice is a commodity, but no two lots are the same. Grain length, moisture, broken percentage and aroma change from mill to mill and from one crop to the next. Our job is to make sure those differences never reach your customers.
                        </p>

                        <p>
                            We work with a network of mills across the main growing regions, buy on specification and test each lot before it is packed. When a lot does not meet the grade, it does not ship.
                        </p>

                        <p>
                            From a single truckload to a regular container programme, you deal with one trade desk that knows your specification, your packing and your delivery window.
                        </p>
                    </div>
                </Container>
            </section>

            <section id='values' aria-labelledby='values-title' className='border-y border-border bg-secondary/60 py-20 sm:py-24 lg:py-28'>
                <Container>
                    <SectionHeading
                        id='values-title'
                        eyebrow='What we stand for'
                        title='How we earn repeat orders'
                    />

                    <ul className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                        {VALUES.map(value => (
                            <li key={value.title} className='rounded-2xl border border-border bg-card p-7 shadow-sm'>
                                <span className='flex size-12 items-center justify-center rounded-xl bg-primary text-gold'>
                                    <value.icon className='size-6' aria-hidden='true' />
                                </span>

                                <h3 className='mt-6 text-lg leading-snug font-bold text-foreground'>
                                    {value.title}
                                </h3>

                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                    {value.text}
                                </p>
                            </li>
                        ))}
                    </ul>
                </Container>
            </section>

            <ProcessSection />
            <CtaSection />
        </Layout>
    )
}

export default About
